class VoiceCommandProcessor {
    constructor() {
        this.commands = {
            // Navigation commands
            navigation: [
                'move to next step', 'move on to next step', 'go to next step', 'navigate to next step',
                'move to previous step', 'go to previous step', 'navigate to previous step',
                'go to step', 'move to step', 'navigate to step',
                'next step', 'next', 'continue', 'go to next',
                'previous step', 'previous', 'go back', 'back',
                'start cooking', 'begin', 'start',
                'finish', 'complete', 'done', 'end'
            ],
            
            // Timer commands
            timer: [
                'start timer', 'set timer', 'timer for', 'start a timer',
                'stop timer', 'pause timer', 'cancel timer',
                'timer', 'timing', 'time'
            ],
            
            // Ingredient commands
            ingredients: [
                'using', 'used', 'added', 'put in', 'added to',
                'ingredient', 'ingredients', 'flour', 'sugar', 'salt',
                'pepper', 'oil', 'butter', 'eggs', 'milk', 'water'
            ],
            
            // Question commands
            questions: [
                'what', 'how', 'why', 'when', 'where', 'can you',
                'help', 'explain', 'tell me', 'show me'
            ]
        };

        this.ingredientPatterns = [
            /(\d+(?:\.\d+)?)\s*(cups?|tablespoons?|teaspoons?|tbsp|tsp|oz|ounces?|pounds?|lbs?|grams?|g|ml|milliliters?|liters?|l)\s+of\s+(.+)/i,
            /(\d+(?:\.\d+)?)\s*(.+)/i,
            /(a|an|some|the)\s+(.+)/i
        ];
    }

    // Process voice command and return structured command object
    processCommand(transcript, isTriggered) {
        if (!isTriggered) {
            return null; // Only process commands after trigger phrase
        }

        const command = {
            type: 'unknown',
            action: null,
            parameters: {},
            confidence: 0,
            originalText: transcript
        };

        // Remove trigger phrase from transcript (handles multiple variations)
        const cleanTranscript = transcript.replace(/hey\s+(4rked|forked|four\s+ked)\s*/gi, '').trim();
        
        if (!cleanTranscript) {
            return null;
        }

        // Check for question commands FIRST - questions take priority
        // If it's a question, it should NOT be treated as a navigation command
        // Be very explicit about question detection
        const hasQuestionWords = cleanTranscript.includes('what') || 
                                cleanTranscript.includes('tell me') || 
                                cleanTranscript.includes('show me') || 
                                cleanTranscript.includes('explain') ||
                                cleanTranscript.includes('how') ||
                                cleanTranscript.startsWith('what\'s') ||
                                cleanTranscript.startsWith('what is');
        
        const isQuestion = this.matchesCommand(cleanTranscript, this.commands.questions) || 
                          this.isQuestionLike(cleanTranscript) ||
                          hasQuestionWords;
        
        if (isQuestion) {
            command.type = 'question';
            command.action = 'ask_question';
            command.parameters = { question: cleanTranscript };
            command.confidence = 0.9; // Higher confidence for questions
        }
        // Check for navigation commands (only if not a question)
        else if (this.matchesCommand(cleanTranscript, this.commands.navigation)) {
            const navAction = this.extractNavigationAction(cleanTranscript);
            // Only treat as navigation if extractNavigationAction returns a valid action
            if (navAction && navAction !== 'unknown') {
                command.type = 'navigation';
                command.action = navAction;
                // If action is an object with stepIndex, store it in actionData
                if (typeof navAction === 'object' && navAction.stepIndex !== undefined) {
                    command.action = navAction.type;
                    command.actionData = { stepIndex: navAction.stepIndex };
                }
                command.confidence = 0.8;
            } else {
                // Not a clear navigation command, treat as question
                command.type = 'question';
                command.action = 'ask_question';
                command.parameters = { question: cleanTranscript };
                command.confidence = 0.7;
            }
        }
        // Check for timer commands
        else if (this.matchesCommand(cleanTranscript, this.commands.timer)) {
            command.type = 'timer';
            command.action = this.extractTimerAction(cleanTranscript);
            command.parameters = this.extractTimerParameters(cleanTranscript);
            command.confidence = 0.7;
        }
        // Check for ingredient commands (but only if not already classified)
        else if (this.matchesCommand(cleanTranscript, this.commands.ingredients)) {
            command.type = 'ingredients';
            command.action = 'track_ingredient';
            command.parameters = this.extractIngredientParameters(cleanTranscript);
            command.confidence = 0.6;
        }

        return command.confidence > 0 ? command : null;
    }

    // Check if transcript matches any command patterns
    matchesCommand(transcript, commandList) {
        return commandList.some(command => 
            transcript.toLowerCase().includes(command.toLowerCase())
        );
    }

    // Extract navigation action
    extractNavigationAction(transcript) {
        const text = transcript.toLowerCase();
        
        // Check for explicit navigation commands first (must have "go to", "move to", "navigate to")
        if (text.includes('go to step') || text.includes('move to step') || text.includes('navigate to step')) {
            // Extract step number
            const stepMatch = text.match(/step\s+(\d+)/);
            if (stepMatch) {
                const stepNum = parseInt(stepMatch[1]);
                return { type: 'go_to_step', stepIndex: stepNum - 1 }; // Convert to 0-based
            }
        }
        
        // Check for "move to next" or "move on to next" (explicit navigation)
        // Handle variations: "move on to the next step", "move on to next step", etc.
        if (text.includes('move to next') || text.includes('move on to next') || text.includes('go to next step')) {
            return 'next_step';
        }
        
        // Check for "move to previous" (explicit navigation)
        if (text.includes('move to previous') || text.includes('go to previous step')) {
            return 'previous_step';
        }
        
        // For ambiguous cases like "next step" or "next", only treat as navigation if it's clearly a command
        // If it contains "what" or "tell me", it's a question, not a command
        if (text.includes('what') || text.includes('tell me') || text.includes('show me') || text.includes('explain')) {
            return null; // Not a navigation command, it's a question
        }
        
        // Only treat as navigation if it's clearly a command phrase
        if ((text.includes('next') || text.includes('continue')) && !text.includes('what')) {
            return 'next_step';
        } else if ((text.includes('previous') || text.includes('back')) && !text.includes('what')) {
            return 'previous_step';
        } else if (text.includes('start') || text.includes('begin')) {
            return 'start_cooking';
        } else if (text.includes('finish') || text.includes('complete') || text.includes('done')) {
            return 'finish_cooking';
        }
        
        return 'unknown';
    }

    // Extract timer action and parameters
    extractTimerAction(transcript) {
        const text = transcript.toLowerCase();
        
        if (text.includes('start') || text.includes('set')) {
            return 'start_timer';
        } else if (text.includes('stop') || text.includes('pause') || text.includes('cancel')) {
            return 'stop_timer';
        }
        
        return 'unknown';
    }

    // Extract timer parameters (duration, description)
    extractTimerParameters(transcript) {
        const durationMatch = transcript.match(/(\d+)\s*(minutes?|mins?|seconds?|secs?|hours?|hrs?)/i);
        const description = transcript.replace(/(\d+)\s*(minutes?|mins?|seconds?|secs?|hours?|hrs?)/gi, '').trim();
        
        let duration = 0;
        if (durationMatch) {
            const value = parseInt(durationMatch[1]);
            const unit = durationMatch[2].toLowerCase();
            
            if (unit.includes('minute') || unit.includes('min')) {
                duration = value * 60; // Convert to seconds
            } else if (unit.includes('second') || unit.includes('sec')) {
                duration = value;
            } else if (unit.includes('hour') || unit.includes('hr')) {
                duration = value * 3600; // Convert to seconds
            }
        }

        return {
            duration,
            description: description || 'Voice timer',
            unit: durationMatch ? durationMatch[2] : 'minutes'
        };
    }

    // Extract ingredient parameters
    extractIngredientParameters(transcript) {
        for (const pattern of this.ingredientPatterns) {
            const match = transcript.match(pattern);
            if (match) {
                if (pattern === this.ingredientPatterns[0]) {
                    // "2 cups of flour" format
                    return {
                        amount: parseFloat(match[1]),
                        unit: match[2],
                        ingredient: match[3].trim()
                    };
                } else if (pattern === this.ingredientPatterns[1]) {
                    // "2 flour" format
                    return {
                        amount: parseFloat(match[1]),
                        unit: 'units',
                        ingredient: match[2].trim()
                    };
                } else if (pattern === this.ingredientPatterns[2]) {
                    // "a cup of flour" format
                    return {
                        amount: 1,
                        unit: 'units',
                        ingredient: match[2].trim()
                    };
                }
            }
        }

        // Fallback: extract any ingredient-like words
        const words = transcript.split(' ');
        const ingredientWords = words.filter(word => 
            word.length > 2 && 
            !['the', 'and', 'or', 'but', 'with', 'for', 'from', 'to', 'in', 'on', 'at'].includes(word.toLowerCase())
        );

        return {
            amount: 1,
            unit: 'units',
            ingredient: ingredientWords.join(' ')
        };
    }

    // Check if text looks like a question
    isQuestionLike(text) {
        const questionIndicators = [
            'what', 'how', 'why', 'when', 'where', 'which', 'who',
            'can you', 'could you', 'would you', 'should i', 'do i',
            'is it', 'are you', 'will you', 'help me', 'explain',
            'tell me', 'show me', 'i need', 'i want', 'i don\'t know',
            'confused', 'stuck', 'trouble', 'problem', 'question',
            'should i use', 'should i save', 'should i add', 'should i put',
            'right now', 'for later', 'save some', 'use all', 'use some'
        ];
        
        const lowerText = text.toLowerCase();
        return questionIndicators.some(indicator => lowerText.includes(indicator));
    }

    // Get available commands for help
    getAvailableCommands() {
        return {
            navigation: [
                'Next step', 'Previous step', 'Start cooking', 'Finish cooking'
            ],
            timer: [
                'Start timer for X minutes', 'Stop timer', 'Set timer'
            ],
            ingredients: [
                'I\'m using 2 cups of flour', 'Added salt', 'Using butter'
            ],
            questions: [
                'What is this technique?', 'How do I do this?', 'Help me with this step',
                'What temperature should I use?', 'How much is 1 cup in ml?',
                'Can I substitute butter for oil?', 'How do I know when it\'s done?',
                'What\'s the next step?', 'I\'m confused about this step'
            ]
        };
    }
}

// Create singleton instance
const voiceCommandProcessor = new VoiceCommandProcessor();

export default voiceCommandProcessor;
